import { HttpErrorResponse } from '@angular/common/http';
import { ChangeDetectorRef, Component, DestroyRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { ConfirmationDialogComponent } from '@components/dialogs/confirmation-dialog/confirmation-dialog.component';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import { LoaderModule, ScientificNotationCellComponent } from '@mds/ui';
import { PIVOT_MANAGER, REPORT_HISTORY } from '@services/apps-data';
import { GlobalDataService } from '@services/global-data.service';
import { GenericApiService } from '@services/generic-api.service';
import { ReportApiService } from '@services/report-api.service';
import { MenuService } from '@services/menu.service';
import { ReusableService } from '@services/reusable.service';
import { ToastrService } from '@services/toaster.service';
import { ToolbarEventService } from '@services/toolbar-event.service';
import { sortBy } from '@utils/sort-by';
import { CellKeyDownEvent, FullWidthCellKeyDownEvent } from 'ag-grid-community';
import { PerfectScrollbarModule } from 'perfect-scrollbar-angular';
import { exhaustMap, filter, finalize, startWith, Subject, takeUntil, takeWhile, timer } from 'rxjs';
import { ReportContentComponent } from './report-content/report-content.component';
import {
  BackgroundReport,
  ReportInfo,
  ReportJobResponse,
  ReportPollingResponse,
  ReportsFlattenedData,
  ReportType,
  ReportTypes,
  SidePanelData,
  TemplateReportModel
} from './report-detail.model';
import { ReportSidepanelComponent } from './report-sidepanel/report-sidepanel.component';
import { RowClassParams } from 'ag-grid-enterprise';

const RED_TINT = { background: '#FFEBEE' };

@Component({
  selector: 'app-report-detail',
  standalone: true,
  imports: [
    MatSidenavModule,
    FormsModule,
    ReactiveFormsModule,
    MdsIconsModule,
    LoaderModule,
    ReportSidepanelComponent,
    PerfectScrollbarModule,
    RouterModule,
    ReportContentComponent
  ],
  templateUrl: './report-detail.component.html',
  styleUrls: ['./report-detail.component.scss'],
  providers: [GenericApiService, ReusableService, ToastrService, GlobalDataService, ToolbarEventService]
})
export class ReportDetailComponent implements OnInit {
  @ViewChild('drawerContent') private drawerContent: HTMLElement;
  private menuService = inject(MenuService);
  private destroyRef = inject(DestroyRef);
  icons = MdsIcon;

  reportType: any;
  templateApiResponse: TemplateReportModel;

  runInBackground: any;
  loadingReportData: boolean;

  @ViewChild('drawer', { static: true }) public drawer!: MatDrawer;
  currentView: string;
  reportTemplate: any;
  reportForm: any;
  templateBody: any;
  sidePanelData: SidePanelData;
  reportPayload: any;
  reportUrl: string;
  reportGridData: any;
  gridOptions: any;
  pivot_setting: any;
  selectedRow = [];
  isQaModeOn: boolean;

  rid: string;
  reportRid: any;
  parameters: any[] = [];

  isSidePanelShow: boolean;
  public ngxEventBus = [];
  private cancelSubscription$ = new Subject<void>();
  loadingMessage: string;
  generatedReportLink: string;

  constructor(
    private genericApiService: GenericApiService,
    private reportApiService: ReportApiService,
    private reusableService: ReusableService,
    private toastrService: ToastrService,
    private route: ActivatedRoute,
    private globalDataService: GlobalDataService,
    private router: Router,
    private dialog: MatDialog,
    private cdr: ChangeDetectorRef
  ) {
    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.loadingMessage = null;
        if (event.url.includes('?id')) return;
        this.reportUrl = null;
        this.cancelSubscription$.next();
        this.templateApiResponse = null;
        this.loadingMessage = '';
        this.generatedReportLink = null;
        this.setTemplateInService(null);
      }
    });
  }

  isNumber(value: any): boolean {
    return !isNaN(parseFloat(value)) && isFinite(value);
  }

  setQueryParams(params) {
    for (const item in params.params) {
      const paramValue = this.isNumber(params.params[item]) ? +params.params[item] : params.params[item];
      const data = { key: item, value: paramValue };
      this.parameters.push(data);
    }
  }

  ngOnInit() {
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (value?.key === 'create') {
        this.createReportTemplate();
      } else if (value?.key === 'save') {
        this.saveGridSettings();
      }
    });
    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
      if (!value) return;
      if (this.reportUrl) {
        if (this.reportUrl.includes('background') || this.isGeneratedReport) {
          this.handleBackgroundReport();
        } else {
          this.startReportGeneration();
        }
      } else {
        this.init();
      }
    });
    this.handleRouteNavigation();
  }

  handleRouteNavigation() {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        startWith(null),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        const snapshot = this.route.snapshot;
        const params = snapshot.paramMap;
        const queryParams = snapshot.queryParamMap;

        const paramRid = params.get('rid');
        const templateId = queryParams.get('id');

        this.rid = paramRid;

        if (templateId) {
          this.setQueryParams(queryParams);
          this.resetSidePanel();
          this.getReportOrTemplateData();
        } else if (paramRid && paramRid !== 'template') {
          this.getReportOrTemplateData();
        }
      });
  }

  getReportOrTemplateData(): void {
    this.initToolbar();
    this.init();
  }

  gridKeyPressed(gridEvent: CellKeyDownEvent | FullWidthCellKeyDownEvent) {
    const keyboardEvent: KeyboardEvent = gridEvent.event as KeyboardEvent;
    if (keyboardEvent.code === 'Enter') {
      this.cellClicked(gridEvent);
    }
  }

  init(): void {
    this.resetState();
    this.parameters = [];
    const rawParams = this.getUrlParams();

    if (rawParams.background) {
      this.runInBackground = rawParams.background;
      delete rawParams.background;
    }

    const paramEntries = Object.entries(rawParams);
    this.setReportURL(paramEntries);
    this.populateParametersFromUrl(rawParams);
    if (paramEntries.length > 0) {
      this.handleUrlParams(paramEntries);
    } else if (this.rid) {
      this.currentView = 'grid';
      this.getReportBody();
    }

    if (this.shouldFetchSchema(this.rid) && !this.isGeneratedReport) {
      this.getReportSchema(this.rid);
    }
  }

  private populateParametersFromUrl(params: Record<string, string>): void {
    for (const [key, rawValue] of Object.entries(params)) {
      const value = this.isNumber(rawValue) ? +rawValue : rawValue;
      this.parameters.push({ key, value });
    }
  }

  private setReportURL(params: [string, string][]) {
    let url = `/${this.rid}`;
    params.forEach(([key, value]) => {
      if (!url?.includes('?')) {
        url += '?';
      }
      url += `${key}=${value}&`;
      this.reportRid = value;
    });
    this.reportUrl = url.replace(/&$/, '');
  }

  private resetState(): void {
    this.reportForm = undefined;
    this.reportTemplate = undefined;
    this.reportUrl = undefined;
    this.resetSidePanel();
  }

  private getUrlParams(): Record<string, string> {
    const urlSearchParams = new URLSearchParams(window.location.search);
    const params = Object.fromEntries(urlSearchParams.entries());
    delete params.csv;
    return params;
  }

  private get isGeneratedReport(): boolean {
    // Using URLSearchParams makes this more robust than simple string includes
    const params = new URLSearchParams(this.reportUrl.split('?')[1]);
    return !this.reportUrl.includes('template') && params.has('id');
  }

  private handleUrlParams(params: [string, string][]): void {
    this.currentView = 'grid';
    if (this.reportUrl.includes('background') || this.isGeneratedReport) {
      this.handleBackgroundReport();
    } else if (this.reportUrl.includes('template')) {
      this.showReportTemplate(params[0][1]);
    } else if (this.reportUrl) {
      this.startReportGeneration();
    }
  }

  private handleBackgroundReport(): void {
    this.loadingReportData = true;
    this.loadingMessage = 'Fetching your generated report. This may take a few moments.';
    this.getReportGridDataObservable(this.reportUrl)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        takeUntil(this.cancelSubscription$),
        finalize(() => {
          this.loadingReportData = false;
        })
      )
      .subscribe({
        next: (response: BackgroundReport) => {
          this.reportRid = response.report_rid;
          this.processReportResponse(response as any);
          this.getReportData(response.report_rid, response.grid_settings);
        },
        error: (errorObj: HttpErrorResponse) => {
          this.loadingMessage = null;
          this.toastrService.showMessage(errorObj?.error?.message || 'Something went wrong. Please try later.', 6000);
        }
      });
  }

  private shouldFetchSchema(rid: string): boolean {
    return rid !== 'template' && rid !== 'background';
  }

  // function to open saved report template
  showReportTemplate(rid: string): void {
    this.reportApiService.getReportTemplate(rid).subscribe((res: TemplateReportModel[]) => {
      const template = res?.[0];
      if (!template) return;
      const templateDataResponse = { ...template, type: ReportType.template };
      this.reportType = template.report_name;
      this.templateApiResponse = templateDataResponse;
      this.setSidePanelData(template);
      this.setTemplateInService(this.templateApiResponse);
      this.renderGridIfAvailable(template);
      this.openSidePanel(); // only once
    });
  }

  private setSidePanelData(template: TemplateReportModel): void {
    this.sidePanelData = {
      actions: template.actions,
      isFrom: 'pivot-manager',
      type: template.report_name,
      category: template.report_category,
      name: template.title,
      reportRid: template.report_rid,
      inputs: template.formSchema,
      parameters: template.schema_argument,
      background: true,
      userTemplateRid: template.rid
    };
  }

  private setTemplateInService(responseData: ReportTypes): void {
    this.reusableService.setTemplateReportData(responseData);
  }

  private renderGridIfAvailable(template: TemplateReportModel): void {
    if (template.lines?.length > 0) {
      this.reportGridData = template;
      this.showAgGrid();
    }
  }

  toggleSidePanel(): void {
    this.drawer?.toggle();
  }

  initToolbar() {
    const layers = [PIVOT_MANAGER, REPORT_HISTORY];
    this.globalDataService.setMegaMenuList(layers);
    this.globalDataService.setActiveApp(layers[0]);
    this.globalDataService.setUrlParam({ rid: this.rid });
    this.globalDataService.setCurrentActiveApp({ name: 'app.reportsList' });
    this.menuService.setMenuConfig({
      activeApp: {
        mainIcon: {
          icon: MdsIcon.APP_PIVOT_MANAGER,
          pathColors: ['#FFF']
        },
        rid: '',
        name: 'Report Explorer',
        icons: [],
        subtitle: '',
        menuTrigger: 'selectApps'
      },
      centerActions: [
        {
          label: 'History',
          key: 'history',
          icon: MdsIcon.HISTORY,
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: true
        },
        {
          label: 'Create Template',
          key: 'create',
          icon: MdsIcon.ADD_TEMPLATE,
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: true
        },
        {
          label: 'Show Chart',
          key: 'show_chart',
          icon: MdsIcon.DATA_USAGE,
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: false
        },
        {
          label: 'Save Settings',
          key: 'save',
          icon: MdsIcon.SAVE,
          selected: false,
          disabled: false,
          menuTrigger: '',
          visible: false
        }
      ]
    });
    // show create template button only in pivot manager configurable reports and saved template. don't show button in stored reports and report history
    this.ngxEventBus.push(
      this.globalDataService.getActiveApp().subscribe((app) => {
        if (app.name === 'reports' && !this.router.url.includes('background')) {
          this.menuService.updateMenuItem('create', { visible: true });
        }
        if (app.name === 'reports' && this.router.url.includes('background')) {
          this.menuService.updateMenuItem('save', { visible: true });
        }
      })
    );
  }

  getReportBody() {
    this.reportApiService.getReportsBody().subscribe((res) => {
      this.templateBody = res;
    });
  }

  getReportData(rid: string, gridSettings?: string | null): void {
    this.reportApiService.getReportSchema(rid).subscribe((res: ReportInfo) => {
      res.type = ReportType.report;
      if (gridSettings) {
        res.grid_settings = gridSettings;
      }
      this.setTemplateInService(res);
    });
  }
  getReportSchema(rid: string): void {
    this.resetSidePanel();

    this.reportApiService.getReportSchema(rid, this.runInBackground).subscribe((res: ReportInfo) => {
      this.setReportTemplateData(res, rid);
      this.updateSidePanelData(res, rid);
      this.setTemplateInService(this.reportTemplate);
      this.openSidePanel();
    });
  }

  private resetSidePanel(): void {
    this.sidePanelData = undefined;
    this.reportGridData = undefined;
  }

  private setReportTemplateData(res: any, rid: string): void {
    this.reportTemplate = res;
    this.reportForm = res.inputs;
    this.reportType = res.name;
    this.reportTemplate.type = 'Report';
  }

  private updateSidePanelData(res: any, rid: string): void {
    this.sidePanelData = {
      ...res,
      isFrom: 'pivot-manager',
      type: res.card_type_label,
      name: res.title,
      reportRid: rid,
      powerBIUrl: undefined,
      parameters: this.parameters
    };
  }

  private createReportData(event): void {
    this.reportPayload = event.payload;
    this.reportUrl = event.url;
    this.startReportGeneration();
  }

  private startReportGeneration(): void {
    this.reportGridData = null;
    this.cancelSubscription$.next();
    this.cancelSubscription$.complete();
    this.cancelSubscription$ = new Subject<void>();
    this.getInitialReportData();
  }

  private getInitialReportData(): void {
    this.loadingReportData = true;
    this.loadingMessage =
      "Generating your report. If it's ready quickly, it'll appear here. Otherwise, it'll run in the background and we'll email you.";
    this.getReportGridDataObservable(this.reportUrl)
      .pipe(takeUntilDestroyed(this.destroyRef), takeUntil(this.cancelSubscription$))
      .subscribe({
        next: (val: ReportJobResponse) => {
          this.loadingMessage = val.message;
          if (val.keep_polling) {
            this.startPolling(val);
          } else {
            this.initializeData();
          }
        },
        error: (errorObj: HttpErrorResponse) => {
          this.loadingReportData = false;
          this.loadingMessage = null;
          this.toastrService.showMessage(errorObj?.error?.message || 'Something went wrong. Please try later.', 6000);
        }
      });
  }

  private startPolling(val: ReportJobResponse): void {
    const jobRid = val?.job_rid?.replace('#', '');
    if (!jobRid) return;
    timer(0, 10000) // ⏱ every 10 seconds
      .pipe(
        exhaustMap(() => this.reportApiService.pollReportData(jobRid)),
        takeWhile((response: ReportPollingResponse) => response?.keep_polling, true),
        takeUntil(this.cancelSubscription$),
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.initializeData();
        })
      )
      .subscribe({
        next: (res: ReportPollingResponse) => {
          this.handlePollingResponse(res);
        },
        error: (errorObj: HttpErrorResponse) => {
          this.toastrService.showMessage(errorObj.error.message || 'Something went wrong. Please try later.', 6000);
        }
      });
  }

  private handlePollingResponse(res: ReportPollingResponse): void {
    if (res) {
      if (res.keep_polling) return;
      if (res.link) {
        this.generatedReportLink = res.link;
        this.navigateToReport(res);
        this.loadingMessage = null;
        this.templateBody = null;
      } else {
        this.loadingMessage = res.error || res.timeout_message;
      }
    }
  }

  private navigateToReport(res: ReportPollingResponse) {
    if (res.link) {
      this.router.navigateByUrl(res.link);
    }
  }

  private initializeData(): void {
    this.loadingReportData = false;
  }

  private processReportResponse(res: ReportsFlattenedData, refresh?: boolean): void {
    if (!res) return;
    this.assignBaseReportData(res);
    this.applyFormSchemaRules();
    this.applyQaMessageIfRequired(res);
    if (this.handleMessageState(res)) return;
    this.handleEmptyLinesState(res, refresh);
    this.prepareSidePanelData(res);
    this.openSidePanel();
  }

  private assignBaseReportData(res: ReportsFlattenedData): void {
    this.reportGridData = res;

    if (this.rid !== 'template') {
      this.reportGridData.type = 'Report';
    }

    if (this.rid === 'background') {
      this.reportType = res.title;
    }

    this.reportGridData.isQaMode = this.isQaModeOn;
  }

  private applyFormSchemaRules(): void {
    const { formSchema, parameters } = this.reportGridData;

    if (!formSchema?.length || !parameters?.length || this.rid != 'background') return;

    formSchema.forEach((form) => {
      if (['tile-autocomplete', 'tile-autoselect'].includes(form.formtype)) {
        form.fieldtype = 'non-editable';
      } else {
        form.formtype = 'disabled';
      }
    });
  }

  private applyQaMessageIfRequired(res: ReportsFlattenedData): void {
    if (
      !res.message &&
      this.isQaModeOn &&
      !res.lines?.length &&
      ['multi_impact_report', 'gwp_report'].includes(this.rid)
    ) {
      res.message = 'Product data is correct';
    }
  }

  private handleMessageState(res: ReportsFlattenedData): boolean {
    if (!res.message) return false;

    if (!this.reportGridData.parameters?.length) {
      this.reportGridData.formSchema = [];
    }

    this.toastrService.showMessage(res.message, 4000);
    return true;
  }

  private handleEmptyLinesState(res: ReportsFlattenedData, refresh: boolean): void {
    if (!res?.lines?.length) {
      if (!this.loadingReportData) {
        this.toastrService.showMessage('No data to show.', 3000);
      }
    }

    if (!refresh) {
      this.showAgGrid();
    }
  }
  private prepareSidePanelData(res: ReportsFlattenedData): void {
    this.sidePanelData = {
      actions: res?.actions,
      isFrom: 'pivot-manager',
      type: this.reportGridData.card_type_label,
      name: res.title,
      reportRid: this.rid === 'template' ? this.templateApiResponse.report_rid : this.rid,
      inputs: this.reportGridData.formSchema,
      parameters: this.reportGridData.parameters || this.parameters,
      background: true, // this is a indicator for reports from history or  background
      powerBIUrl: res.powerBIUrl,
      backgroundReportRid: this.reportGridData.backgroundReportRid
    };
  }

  reloadReport() {
    this.startReportGeneration();
  }

  cellApiCall(api, node) {
    const url = `/api/v1/${api.url}`;
    const payload = JSON.parse(api.payload || '{}');
    this.genericApiService.linkApiCall(api.method, url, payload).subscribe(
      () => {
        const row = node.data.row.filter((_row) => _row.key === node.col);
        row.link = null;
        node.setDataValue(node.col, 'Updated');
        node.setSelected(true);
      },
      (error) => {}
    );
  }

  getHeaderDefs(data, lines?: any[]) {
    const HeaderData = [];

    data.forEach((d) => {
      const obj: any = {
        headerName: d.label,
        field: d.key.split('.')[1] ? d.key.split('.')[1] : d.key,
        width: 125,
        comparator: (valueA, valueB) => `${valueA}`.localeCompare(`${valueB}`),
        hide: !!d.hidden,
        enableValue: typeof d.value === 'number'
      };
      let values = d.display_value ? d.display_value : d.value;

      if (typeof d.value === 'number') {
        values = d.value;
      } else if (typeof d.value === 'boolean') {
        values = d.value;
      } else if (!values) {
        values = this.checkFieldValueInTenRows(lines, d);
      }

      if (['stype', 'in', 'substance', 'in_intermediate_out', 'product'].includes(obj.field)) {
        obj.width = 250;
      }

      if (this.pivot_setting?.values && this.pivot_setting?.values.some((e) => e.includes(`(${obj.field})`))) {
        const aggType = this.pivot_setting.values.find((e) => e.includes(`(${obj.field})`));

        obj.aggFunc = /(.*?)\((.*?)\)/g.exec(aggType)[1];
      }

      if (this.pivot_setting?.row_groups && this.pivot_setting?.row_groups.includes(obj.field)) {
        obj.rowGroup = true;
      }

      if (this.pivot_setting?.column_labels && this.pivot_setting?.column_labels.includes(obj.field)) {
        obj.pivot = true;
      }

      if (typeof values === 'number') {
        obj.cellRenderer = ScientificNotationCellComponent;
        obj.comparator = (num1, num2) => {
          if (num1 === null && num2 === null) return 0;
          if (isNaN(num1) || num1 === null) return -1;
          if (isNaN(num2) || num2 === null) return 1;
          return num1 - num2;
        };
      }

      obj.cellStyle = (params) => {
        const cellobj: any = {
          color: '',
          'background-color': ''
        };

        if (params.data) {
          const colField = params.colDef.field;
          const { row } = params.data;
          for (const i in row) {
            if (row[i].key === colField) {
              if (row[i].hide_display_value) {
                cellobj.visibility = 'hidden';
              }

              if (row[i].link) {
                cellobj.color = '#1960E2';
                cellobj.cursor = 'pointer';
              } else {
                cellobj.color = row[i].color;
                cellobj['background-color'] = row[i].bgcolor;
                break;
              }
            }
          }
        }

        return cellobj;
      };

      obj.onCellClicked = this.cellClicked.bind(this);

      obj.cellClassRules = {
        'custom-link': function (params) {
          let flag = false;
          if (params.data) {
            const colField = params.colDef.field;
            const { row } = params.data;
            for (const i in row) {
              if (row[i].key === colField && row[i].link) {
                flag = true;
              }
            }
          } else {
            return false;
          }
          return flag;
        },
        noselect() {
          return true;
        }
      };

      if (typeof values !== 'number') {
        obj.cellClass = 'stringType';
      }

      obj.filter = typeof values === 'number' ? 'agNumberColumnFilter' : 'agTextColumnFilter';

      if (['type', 'type_label', 'data_type'].includes(obj.field)) {
        obj.filter = 'agSetColumnFilter';
      }

      if (obj.field !== 'line_color') {
        HeaderData.push(obj);
      }
    });

    return HeaderData;
  }

  /**
   * This function will check the field value in rows until and unless
   * value is found then it will used to select appropriate filter type of particular column.
   * (so appropriate field filter type can be set)
   * @param lines
   * @param nullOrEmptyValueObject
   * @returns
   */
  checkFieldValueInTenRows(lines: any[], nullOrEmptyValueObject: any) {
    let value = null;
    if (!lines.length) return null;
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const nextRowObject = line.find((item) => item.key === nullOrEmptyValueObject.key);
      const lineValue = nextRowObject?.value || nextRowObject?.display_value;
      if (lineValue || typeof lineValue === 'number') {
        value = lineValue;
        break;
      }
    }
    return value;
  }

  getRowData(data) {
    const rowData = [];
    data.forEach((e) => {
      const rowObj: any = {};
      rowObj.row = e;
      e.forEach((d) => {
        let keyList = [];
        if (d.key.includes('.')) {
          keyList = d.key.split('.');
        } else if (d.key.includes('$')) {
          keyList = d.key.split('$');
        } else {
          keyList.push(d.key);
        }

        const key = keyList[keyList.length - 1];
        if (key === 'closest_match_fit_score') {
          if (d.display_value === 'Unknown' || d.value == null) {
            rowObj[key] = '';
          } else {
            rowObj[key] = d.display_value;
          }
        } else if (key === 'is_private' || key === 'outputGp' || key === 'rid') {
          rowObj[key] = d.value;
        } else if (typeof d.value === 'number') {
          rowObj[key] = d.value;
        } else {
          rowObj[key] = d.display_value;
        }

        if (d.key === 'line_color') {
          rowObj[key] = d;
        }
      });
      rowData.push(rowObj);
    });
    return rowData;
  }

  showAgGrid() {
    const alreadyAddedKey = new Set();
    const headerData = this.reportGridData.lines.flatMap((row, rowIndex) =>
      row
        .filter((cell) => !alreadyAddedKey.has(cell.key) && alreadyAddedKey.add(cell.key))
        .map((cell) => ({ ...cell, rowIndex }))
    );
    sortBy(headerData, 'sortlevel');
    if (this.reportGridData.pivot_settings) {
      this.pivot_setting = this.reportGridData.pivot_settings;
    }
    const rowData = this.getRowData(this.reportGridData.lines);
    const columnDefs = this.getHeaderDefs.call(this, headerData, this.reportGridData.lines);

    this.gridOptions = {
      defaultColDef: {
        sortable: true,
        resizable: true,
        filter: true,
        enablePivot: true,
        enableRowGroup: true,
        cellDataType: false
      },
      pivotMode: this.pivot_setting?.pivot_mode,
      columnDefs,
      sideBar: {
        toolPanels: [
          {
            id: 'columns',
            labelDefault: 'Columns',
            labelKey: 'columns',
            iconKey: 'columns',
            toolPanel: 'agColumnsToolPanel'
          }
        ]
      },
      rowData,
      suppressCellSelection: true,
      suppressRowClickSelection: true,
      animateRows: true,
      rowSelection: 'multiple',
      getRowStyle: (params: RowClassParams) => {
        const data = params.data;
        if (!data) return {};

        const isZeroOrNull = (v: any) => v === 0 || v === null;
        const hasGwp = data.gwp !== undefined;
        const hasBiogwp = data.biogwp !== undefined;
        const shouldHighlight = (hasGwp && isZeroOrNull(data.gwp)) || (hasBiogwp && isZeroOrNull(data.biogwp));

        return shouldHighlight ? RED_TINT : {};
      }
    };
  }

  // keyboard click [enter]
  cellClicked(params) {
    const { row } = params.data;
    const colField = params.colDef.field;
    row.forEach((e) => {
      if (e.key === colField) {
        if (e.link && e.link.type === 'int') {
          this.checkIfComposeThenNavigateToNewUI(e.link.url);
        } else if (e.link && e.link.type === 'api') {
          params.node.col = params.colDef.field;
          this.cellApiCall(e.link, params.node);
        } else if (e.link && e.link.type === 'ext') {
          this.checkIfComposeThenNavigateToNewUI(e.link.url);
        }
      }
    });
  }

  checkIfComposeThenNavigateToNewUI(link: string) {
    if (link.startsWith('composer/metadata/')) {
      const rid = link.split('composer/metadata/')[1];
      this.router.navigate([`/mattermaps/overview/${rid}/edit`]);
    } else {
      this.router.navigate([`/${link}`]);
    }
  }

  getReportGridDataObservable(url) {
    this.selectedRow = [];
    this.isQaModeOn = Boolean(new URLSearchParams(url).get('qa_mode'));
    return this.reportApiService.getReportGridData(url);
  }

  openSidePanel() {
    this.isSidePanelShow = false;
    this.drawer.open();
  }
  closeSidepanel() {
    this.isSidePanelShow = true;
    this.drawer.close();
  }

  sidepanelEvent(event) {
    const ev = event.value;
    if (ev.type === 'close') {
      this.closeSidepanel();
    } else if (ev.type === 'submit-report') {
      this.createReportData(ev);
    }
  }

  createReportTemplate() {
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        header: 'Create report template',
        text: 'Your template will be saved to the Report Explorer.',
        cancel_text: 'Cancel',
        publish_text: 'Save',
        type: 'createTemplate',
        placeholder: 'Template name',
        fieldConfig: {
          label: 'Template name',
          type: 'text'
        }
      }
    });
    dialogRef.afterClosed().subscribe((name) => {
      if (name) {
        const reportValue = this.globalDataService.getSidepanelData();
        // set data set empty before saving report
        const keysToReplace = ['product', 'process', 'company', 'substance', 'site', 'site_or_company'];
        keysToReplace.forEach((key) => {
          // check condition if type is not autocomplete and it PG/process etc to be null and not RSL/GHS values
          const type = reportValue.formSchema.find((type) => type.key === key && type.formtype !== 'autocomplete');
          if (type && Object.prototype.hasOwnProperty.call(reportValue.value, key)) {
            reportValue.value[key] = null;
          }
        });
        const rid = this.rid === 'template'
          ? this.templateApiResponse.report_rid
          : this.rid === 'background'
            ? this.reportRid
            : this.rid;
        // get data from sidepanel and grid to save the template
        const gridSetting = this.globalDataService.getGridSettings();
        const payload = {
          name,
          report_rid: rid,
          report_type: this.reportType,
          schema_argument: reportValue.value,
          grid_settings: JSON.stringify(gridSetting)
        };
        this.reportApiService.createReportTemplate(rid, payload).subscribe(() => {
          this.toastrService.showMessage('The template has been successfully saved');
          const menuConfigs = this.menuService.menuConfigSubject.getValue();
          this.menuService.setMenuConfig({
            ...menuConfigs
          });
        });
      }
    });
  }

  saveGridSettings() {
    setTimeout(() => {
      const gridSetting = this.globalDataService.getGridSettings();
      const payload = {
        is_grid: true,
        grid_settings: JSON.stringify(gridSetting),
        rid: this.reportRid
      };
      this.reportApiService.updateReportSettings(this.reportRid, payload).subscribe(() => {
        this.toastrService.showMessage('Your grid and chart configuration settings have been saved.');
      });
    }, 2000);
  }
}
